// TW-0171 — a SAVED photo of an ID, chosen from the phone's library rather than taken with its camera.
//
// Excalibur Art Collective (Augusta GA) had a client whose ID was only on their phone, and a phone cannot
// photograph its own screen. So where the shop allows it, the ID step offers "Choose from library" under
// "Take photo" (Form.tsx, FormPhotoUploader), and this is what turns the picked file into the image the
// waiver posts. Two jobs, both about what reaches the API:
//
//   SIZE     a phone's own photo is 3 to 12 MB and 4000px across, and the waiver posts every image as a
//            data URI inside ONE JSON body that the API's Lambda caps at 6 MB. It is drawn down to fit
//            SAVED_PHOTO_MAX_EDGE, a little over what the camera path already posts (1280 wide), so an
//            ID's small print stays readable on the signed PDF and the body stays well under the cap.
//   FORMAT   whatever the phone hands over (a JPEG, a PNG screenshot, a HEIC the browser can decode)
//            comes out as the one JPEG the PDF renderer already draws every day.
//
// A browser decodes an image upright by its EXIF orientation and draws it upright onto a canvas, so a
// portrait photo stays portrait; nothing here rotates by hand.

export const SAVED_PHOTO_MAX_EDGE = 1600;
export const SAVED_PHOTO_QUALITY = 0.85;
/** Past this the phone may not have the memory to decode it at all; the client is told, not frozen. */
export const SAVED_PHOTO_MAX_BYTES = 30 * 1024 * 1024;

export type SavedPhotoResult =
  | { ok: true; dataUri: string }
  | { ok: false; reason: "too_big" | "unreadable" };

/** The size to draw a `width` × `height` image at so neither edge passes `maxEdge`, never enlarged.
 *  Null for a size that is not an image at all (zero, negative, not a number). */
export function fitWithin(width: number, height: number, maxEdge: number): { width: number; height: number } | null {
  if (!(width > 0) || !(height > 0) || !(maxEdge > 0)) return null;
  const scale = Math.min(1, maxEdge / Math.max(width, height));
  return { width: Math.max(1, Math.round(width * scale)), height: Math.max(1, Math.round(height * scale)) };
}

type Source = "camera" | "library";

/** The keys the posted waiver gains when an ID photo came from the phone's LIBRARY, and only then, so a
 *  camera photo's body is the one it has always been. The API prints a line under the photo for each
 *  (app.py, ID_PHOTO_SAVED_LINE): a saved photo is a copy of the ID, and the file should say which kind
 *  of copy it holds. The minor's own ID counts only where the form asked for it. */
export function photoSourceFields(a: {
  idPhoto: string | null;
  idSource: Source;
  minorAsked: boolean;
  minorPhoto: string | null;
  minorSource: Source;
  /** 2026-09-24 — the birth certificate or guardianship papers, the same way: counted only where asked. */
  proofAsked?: boolean;
  proofPhoto?: string | null;
  proofSource?: Source;
  /** TW-0227 — the photo of the other parent's signed permission, the same way: counted only where it is the way the
   *  parent picked, where a city's or county's rule wants both parents' written permission. */
  permissionAsked?: boolean;
  permissionPhoto?: string | null;
  permissionSource?: Source;
  /** 2026-10-08 (TW-0279 slice 8) — the doctor's prescription for a numbing product, the same way: counted only where the
   *  page asked for it, which is a yes to the state's numbing question. */
  prescriptionAsked?: boolean;
  prescriptionPhoto?: string | null;
  prescriptionSource?: Source;
  /** TW-0279 slice 10 — the client's doctor's written instructions on a cosmetic signing, the same way: counted only where
   *  the photo was asked for (cosmetic.ts providerInstructionsNeeded). */
  providerAsked?: boolean;
  providerPhoto?: string | null;
  providerSource?: Source;
}): SourceFields {
  const out: SourceFields = {};
  if (a.idPhoto && a.idSource === "library") out.idPhotoSource = "library";
  if (a.minorAsked && a.minorPhoto && a.minorSource === "library") out.minorIdPhotoSource = "library";
  if (a.proofAsked && a.proofPhoto && a.proofSource === "library") out.guardianProofSource = "library";
  if (a.permissionAsked && a.permissionPhoto && a.permissionSource === "library") out.secondParentPermissionSource = "library";
  if (a.prescriptionAsked && a.prescriptionPhoto && a.prescriptionSource === "library") out.numbingPrescriptionSource = "library";
  if (a.providerAsked && a.providerPhoto && a.providerSource === "library") out.providerInstructionsSource = "library";
  return out;
}

type SourceFields = {
  idPhotoSource?: "library";
  minorIdPhotoSource?: "library";
  guardianProofSource?: "library";
  secondParentPermissionSource?: "library";
  numbingPrescriptionSource?: "library";
  providerInstructionsSource?: "library";
};

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("the browser could not decode this file"));
    img.src = url;
  });
}

/** The picked file as a JPEG data URI, sized for the waiver; or why it could not be. Never throws. */
export async function savedPhotoToDataUri(file: File): Promise<SavedPhotoResult> {
  if (file.size > SAVED_PHOTO_MAX_BYTES) return { ok: false, reason: "too_big" };
  // `type` is empty for some files a phone hands over, so an empty type is tried rather than refused;
  // a type that is plainly something else (a PDF, a video) is not an ID photo
  if (file.type && !file.type.startsWith("image/")) return { ok: false, reason: "unreadable" };
  const url = URL.createObjectURL(file);
  try {
    const img = await loadImage(url);
    const size = fitWithin(img.naturalWidth, img.naturalHeight, SAVED_PHOTO_MAX_EDGE);
    if (!size) return { ok: false, reason: "unreadable" };
    const canvas = document.createElement("canvas");
    canvas.width = size.width;
    canvas.height = size.height;
    const ctx = canvas.getContext("2d");
    if (!ctx) return { ok: false, reason: "unreadable" };
    // a PNG screenshot can be transparent, and a JPEG has no transparency: white, like the paper
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, size.width, size.height);
    ctx.drawImage(img, 0, 0, size.width, size.height);
    const dataUri = canvas.toDataURL("image/jpeg", SAVED_PHOTO_QUALITY);
    // a canvas the browser refused to paint answers "data:," rather than throwing
    return dataUri.startsWith("data:image/jpeg") ? { ok: true, dataUri } : { ok: false, reason: "unreadable" };
  } catch {
    return { ok: false, reason: "unreadable" };
  } finally {
    URL.revokeObjectURL(url);
  }
}
